import { useContext, useEffect, useState } from "react";

import { ContentCopy, Delete, Download, Visibility } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import {
  type MatchReportSummary,
  deleteIntelMatchReport,
  getIntelMatchReport,
  getIntelMatchReportPdf,
  listIntelMatchReport,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { formatDateShort } from "@/common/utils";
import { DisabledComponentDialog } from "@/components/DisabledComponent";
import { parseUserPermissions } from "@/components/UserManagement/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { IUserPermission } from "@/types/typesAlerts";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

const MatchReportsList = () => {
  const { orgId } = useParams();
  const { currentUser } = useContext(AuthStateContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedPageSize = Number(searchParams.get("pageSize"));
  const paginationModel: GridPaginationModel = {
    page: Math.max(Number(searchParams.get("page")) || 1, 1) - 1,
    pageSize: PAGE_SIZE_OPTIONS.includes(requestedPageSize)
      ? requestedPageSize
      : DEFAULT_PAGE_SIZE,
  };
  const reportPermissions =
    currentUser &&
    parseUserPermissions(currentUser?.permissions as IUserPermission[]);
  const canViewEntries = reportPermissions
    ? reportPermissions.canViewEntriesIntel
    : true;
  const [reports, setReports] = useState<MatchReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] =
    useState<MatchReportSummary | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchReports = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const response = await listIntelMatchReport({
        path: { org_id: orgId },
      });
      setReports(response.data?.reports ?? []);
    } catch (error) {
      console.error("Error fetching match reports:", error);
      toast.error("Failed to load match reports");
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", String(model.page + 1));
        next.set("pageSize", String(model.pageSize));
        return next;
      },
      { replace: true },
    );
  };

  const viewReport = async (id: string) => {
    try {
      const response = await getIntelMatchReport({
        path: { id },
      });
      const detail = response.data;
      const firstItem = detail?.items?.[0];
      if (firstItem?.attachment_id) {
        navigate(
          `/rubiintel/${orgId}/match-report/${id}/${firstItem.attachment_id}`,
          { state: { fromList: true } },
        );
      } else {
        toast.error("No attachment found for this match report");
      }
    } catch (error) {
      console.error("Error fetching report detail:", error);
      toast.error("Failed to load report details");
    }
  };

  const sanitizeFilename = (s: string) =>
    s.replace(/[/\\:*?"<>| -]/g, "_").trim();

  const downloadReportPDF = async (report: MatchReportSummary) => {
    setDownloadingId(report.id);
    try {
      const response = await getIntelMatchReportPdf({
        path: { id: report.id },
      });
      const blob = response.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const trimmedTitle = report.title?.trim();
      a.download = trimmedTitle
        ? `${sanitizeFilename(trimmedTitle)}.pdf`
        : `${report.reference_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteClick = (
    e: React.MouseEvent,
    report: MatchReportSummary,
  ) => {
    e.stopPropagation();
    setReportToDelete(report);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!reportToDelete) return;
    setIsDeleting(true);
    try {
      await deleteIntelMatchReport({
        path: { id: reportToDelete.id },
      });
      toast.success("Match report deleted");
      setDeleteModalOpen(false);
      setReportToDelete(null);
      fetchReports();
    } catch (error) {
      console.error("Error deleting match report:", error);
      toast.error("Failed to delete match report");
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [orgId]);

  const columns: GridColDef[] = [
    {
      field: "reference_number",
      headerName: "Reference",
      flex: 1,
      minWidth: 140,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            height: "100%",
          }}
        >
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontFamily: "monospace" }}
          />
          <Tooltip title="Copy reference number">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(params.value);
                toast.success("Reference number copied");
              }}
            >
              <ContentCopy sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 180,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontWeight: params.value ? 500 : 400,
              color: params.value ? "text.primary" : "text.secondary",
            }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "generated_by_name",
      headerName: "Generated By",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "time_created",
      headerName: "Generated",
      flex: 1,
      minWidth: 160,
      valueFormatter: (value: string) => formatDateShort(value),
    },
    {
      field: "item_count",
      headerName: "Items",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "notes",
      headerName: "Notes",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                viewReport(params.row.id);
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download PDF">
            <IconButton
              size="small"
              disabled={downloadingId === params.row.id}
              onClick={(e) => {
                e.stopPropagation();
                downloadReportPDF(params.row);
              }}
            >
              {downloadingId === params.row.id ? (
                <CircularProgress size={18} />
              ) : (
                <Download fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete report">
            <IconButton
              size="small"
              onClick={(e) => handleDeleteClick(e, params.row)}
              sx={{ color: "error.main" }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (!canViewEntries) {
    return (
      <DisabledComponentDialog
        isDialogOpen={true}
        handleClose={() => navigate("/")}
        title="You do not have access to view match reports."
        message={
          <Typography>
            Please contact your administrator to request access.
          </Typography>
        }
      />
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Match Reports
      </Typography>

      <Paper elevation={2} sx={{ mb: 3 }}>
        <DataGrid
          rows={reports}
          columns={columns}
          autoHeight
          onRowClick={(params) => viewReport(params.row.id)}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          initialState={{
            sorting: {
              sortModel: [{ field: "time_created", sort: "desc" }],
            },
          }}
          sx={{
            border: "none",
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
            },
          }}
          localeText={{
            noRowsLabel: "No match reports generated yet",
          }}
        />
      </Paper>

      <Dialog
        open={deleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setReportToDelete(null);
          }
        }}
      >
        <DialogTitle>Delete Match Report</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete match report{" "}
            <strong>{reportToDelete?.reference_number}</strong>? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteModalOpen(false);
              setReportToDelete(null);
            }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : undefined}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MatchReportsList;
